<script setup>
import { ref, defineEmits, onMounted, onBeforeUnmount } from 'vue';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import { Icon } from '@iconify/vue';

const emit = defineEmits(['updateSort']);

const filterOptions = ref([
  { name: 'Sort: Creation Time', field: 'creationOrder'},
  { name: 'Sort: Bump Order', field: 'bumpOrder' },
  { name: 'Sort: Market Cap', field: 'marketCap' },
  { name: 'Sort: Last Reply', field: 'lastReply' },
  { name: 'Sort: Reply Count', field: 'replyCount' },
  // Add more filter options as needed
]);

const selectedOption = ref(filterOptions.value[0]);
const hoveredOption = ref(null);
const menuOpenFilter = ref(false);
const menuOpenOrder = ref(false);


const handleClickOutside = (event) => {
  if (!event.target.closest('.menu-container')) {
    menuOpenFilter.value = false;
    menuOpenOrder.value = false;
  }
};

// toggle order menu
const toggleMenuOrder = () => {
  menuOpenOrder.value = false;
  menuOpenOrder.value = !menuOpenOrder.value;
  menuOpenFilter.value = false;
};
// toggle filter menu
const toggleMenuFilter = () => {
  menuOpenFilter.value = false;
  menuOpenFilter.value = !menuOpenFilter.value;
  menuOpenOrder.value = false;
};

const orderOptions = ref([
  { name: 'Order: Desc', order: 'desc' },
  { name: 'Order: Asc', order: 'asc' },
]);

const selectedOrder = ref(orderOptions.value[0]);

const applyOrderFilter = (option) => {
  selectedOrder.value = option;
  emit('updateSort', { field: selectedOption.value.field , order: option.order });
  menuOpenOrder.value = false; 
};

const applySortFilter = (option) => {
  selectedOption.value = option;
  emit('updateSort', { field: option.field, order: selectedOrder.value.order });
  menuOpenFilter.value = false; 
};

onMounted(() => {
  emit('updateSort', { field: selectedOption.value.field, order: selectedOrder.value.order });

  if (typeof window !== 'undefined') {
    // Add event listener for clicks outside
    document.addEventListener('click', handleClickOutside);
  }
});

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    // Remove event listener for clicks outside
    document.removeEventListener('click', handleClickOutside);
  }
});
</script>

<template>
  <div class="flex justify-center pl-1 lg:pl-0">
    <!-- Sort Filter Menu -->
    <div class="menu-container flex gap-3 flex-wrap w-full justify-center">
      <Menu as="div" class="w-full relative inline-block text-left sm:w-fit">
        <MenuButton @click="toggleMenuFilter" class="w-full relative text-sm bg-primary text-gray-800 group">
          <span class="text-left text-nowrap  w-full sm:w-fit lg:w-full items-center justify-between relative z-10 flex items-center px-3 py-2 h-12 overflow-hidden font-medium text-gray-800 transition-all duration-300 ease-out border-2 border-gray-900 rounded-lg bg-primary transform -translate-y-1 -translate-x-1 group-hover:translate-y-0 group-hover:translate-x-0">
            <span style="pointer-events: none;" class="text-left  font-bold">{{ selectedOption.name }}</span>
            <Icon icon="mdi-light:chevron-down" class="-mr-1 ml-1 h-5 w-5 text-gray-800" aria-hidden="true" />
          </span>
          <span class="absolute bottom-[1px] right-[1px] w-full h-12 bg-gray-900 rounded-lg transition-all duration-300 ease-out group-hover:bg-white"></span>
        </MenuButton>

      <transition enter-active-class="transition ease-out duration-100" enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100" leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
        <MenuItems v-show="menuOpenFilter" class="absolute left-0 z-20 mt-2 w-fit origin-top-right rounded-md bg-primary shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none border-2 border-gray-900 w-full">
          <div>
            <MenuItem v-for="option in filterOptions" :key="option.name" >
              <a @click.prevent="applySortFilter(option)" @mouseover="hoveredOption = option" @mouseleave="hoveredOption = null" href="#" class="text-nowrap" 
              :class="[
                  hoveredOption ? (option.name === hoveredOption?.name ? 'bg-limen' : 'bg-transparent') : '',
                  selectedOption.name === option.name ? 'bg-limen font-bold' : '',
                  'block px-2.5 py-2 text-sm rounded-md'
                ]"
              >
              <span v-if="option.name === selectedOption.name" class="mr-2">✔</span>
              <span class="truncate">{{ option.name }}</span></a>
            </MenuItem>
          </div>
        </MenuItems>
      </transition>
      </Menu>

      <!-- Order Filter Menu -->
      <Menu as="div" class="w-full relative inline-block text-left sm:w-fit">
        <MenuButton @click="toggleMenuOrder" class="w-full relative text-sm bg-primary text-gray-800 group">
            <span class="text-left text-nowrap flex  w-full sm:w-fit lg:w-full items-center justify-between relative z-10 flex items-center px-3 py-2 h-12 overflow-hidden font-medium text-gray-800 transition-all duration-300 ease-out border-2 border-gray-900 rounded-lg bg-primary transform -translate-y-1 -translate-x-1 group-hover:translate-y-0 group-hover:translate-x-0">
              <span style="pointer-events: none;" class=" font-bold">{{ selectedOrder.name }}</span>
              <Icon icon="mdi-light:chevron-down" class="-mr-1 ml-1 h-5 w-5 text-gray-800" aria-hidden="true" />
            </span>
            <span class="absolute bottom-[1px] right-[1px] w-full h-12 bg-gray-900 rounded-lg transition-all duration-300 ease-out group-hover:bg-white"></span>
        </MenuButton>

        <transition enter-active-class="transition ease-out duration-100" enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100" leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
          <MenuItems v-show="menuOpenOrder" class="absolute right-0 md:left-0 z-20 mt-2 w-fit origin-top-right rounded-md bg-primary shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none border-2 border-gray-900 w-full">
            <div>
              <MenuItem v-for="option in orderOptions" :key="option.name">
                <a @click.prevent="applyOrderFilter(option)" @mouseover="hoveredOption = option" @mouseleave="hoveredOption = null" href="#" class="text-nowrap" 
                :class="[
                    hoveredOption ? (option.name === hoveredOption?.name ? 'bg-limen' : 'bg-transparent') : '',
                    selectedOrder.name === option.name ? 'bg-limen font-bold' : '',
                    'block px-2.5 py-2 text-sm rounded-md'
                  ]"
                >
                <span v-if="option.name === selectedOrder.name" class="mr-2">✔</span>
                <span class="truncate">{{ option.name }}</span></a>
              </MenuItem>
            </div>
          </MenuItems>
        </transition>
      </Menu>

      <router-link to="/create" class="relative inline-block text-sm group sm:w-fit w-full">
        <span class="relative z-10 flex text-nowrap items-center px-3 py-2 h-12 overflow-hidden font-medium text-gray-800 transition-all duration-300 ease-out border-2 border-gray-900 rounded-lg bg-primary transform -translate-y-1 -translate-x-1 group-hover:translate-y-0 group-hover:translate-x-0 hover:bg-limen">
          <span class="relative  font-bold">+ Create</span>
        </span>
        <span class="absolute bottom-[1px] right-[1px] w-full h-12 bg-gray-900 rounded-lg transition-all duration-300 ease-out group-hover:bg-white"></span>
      </router-link>

        
    </div>
  
  </div>
</template>

